import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSymbolContext } from '../../../contexts/Symbol-Context.js';
import { useMetricsContext } from '../../../contexts/Metrics-Context.js';
import { useAuthContext } from '../../../contexts/Auth-Context.js';
import { formatFixedTrimmed } from '../../../utils/format.js';
import '../market_order/OrderSLTP.scss';
import s from './StrategyModal.module.scss';

const MIN_REQUIRED_MARGIN = 10;

function displayNum(v, decimals = 8) {
  if (v === null || v === undefined || v === '') return '';
  // Preserve in-progress typing (trailing ".", "0.", "1.0")
  if (
    typeof v === 'string' &&
    (v === '.' || v.endsWith('.') || /^-?\d*\.\d*0+$/.test(v) || /^-?\d*\.$/.test(v))
  ) {
    return v;
  }
  const n = typeof v === 'string' ? Number(v) : v;
  if (!Number.isFinite(n) || n === 0) return '';
  return formatFixedTrimmed(n, decimals);
}

function parseInput(val) {
  const n = Number(val);
  return val.trim() !== '' && Number.isFinite(n) ? n : val;
}

function resolvePipSize(details, tick, symbolInfo) {
  const pipPos = Number(
    details?.pip_position ?? tick?.pip_position ?? symbolInfo?.pip_position
  );
  if (Number.isFinite(pipPos) && pipPos >= 0) return 1 / 10 ** pipPos;
  const digit = Number(details?.digit ?? tick?.digit ?? tick?.d ?? symbolInfo?.digit);
  if (Number.isFinite(digit) && digit > 0) return 1 / 10 ** digit;
  return 0.001;
}

function resolveLeverage(activeLvg, leverage, leverageData) {
  if (Number(activeLvg) > 0) return Number(activeLvg);
  const row = Array.isArray(leverage) ? leverage[0] : leverage;
  const fromCtx = parseFloat(row?.user_default_leverage ?? row?.available_leverage);
  if (Number.isFinite(fromCtx) && fromCtx > 0) return fromCtx;
  const fromAuth = parseFloat(
    leverageData?.user_default_leverage ??
      leverageData?.[0]?.user_default_leverage ??
      leverageData?.available_leverage
  );
  if (Number.isFinite(fromAuth) && fromAuth > 0) return fromAuth;
  return 1;
}

function formatMargin(v) {
  if (!Number.isFinite(v) || v <= 0) return '0';
  if (v < 1) return v.toFixed(5);
  return String(Math.round(v * 10) / 10);
}

/** Persist account PNL to parent (EA API). Keep local display as computed. */
function commitPnl(pnl, onChange) {
  if (!Number.isFinite(pnl) || pnl <= 0) return;
  // Keep cents-ish precision; avoid float noise.
  onChange(String(Math.round(pnl * 10000) / 10000));
}

/**
 * Cross-field SL/TP — formulas mirror OrderSLTP.jsx.
 * Each field is a driver; qty/price/FX changes re-run from that driver (not always from pips).
 */
export default function StrategySltpFields({
  symbol,
  direction,
  quantity,
  slOn,
  tpOn,
  slPnl,
  tpPnl,
  legacySlPips,
  legacyTpPips,
  onSlPnlChange,
  onTpPnlChange,
  onMarginChange,
  toggles,
  marginError,
  slError,
  tpError,
}) {
  const { t } = useTranslation();
  const {
    bidPrice,
    askPrice,
    bidPriceExchangeRate,
    askPriceExchangeRate,
    selectedSymbolExchangeRate,
    selectedSymbolQuoteExchangeRate,
    symbolInfo,
    symbolData,
    symbolDetails,
    leverage,
    activeLvg,
  } = useSymbolContext();
  const { metrics } = useMetricsContext();
  const { user, selectedAuthSymbol, leverageData } = useAuthContext();

  const tick = symbol && symbolData?.[symbol] ? symbolData[symbol] : null;
  const details = (symbol && symbolDetails?.[symbol]) || symbolInfo || {};

  // Same live quote sources as Market-Order (context bid/ask when chart symbol matches)
  const chartMatches = String(selectedAuthSymbol || '') === String(symbol || '');
  const liveBid =
    Number(
      (chartMatches && bidPrice != null && bidPrice !== '' ? bidPrice : null) ??
        tick?.bid ??
        tick?.b ??
        0
    ) || 0;
  const liveAsk =
    Number(
      (chartMatches && askPrice != null && askPrice !== '' ? askPrice : null) ??
        tick?.ask ??
        tick?.a ??
        0
    ) || 0;
  const liveEb =
    Number(
      (chartMatches ? bidPriceExchangeRate : null) ?? tick?.eb ?? 1
    ) || 1;
  const liveEa =
    Number(
      (chartMatches ? askPriceExchangeRate : null) ?? tick?.ea ?? 1
    ) || 1;

  const isBuy = String(direction).toUpperCase() !== 'SELL';
  const entryPrice = Number(isBuy ? liveAsk : liveBid) || 0;
  // Mark used by open/update position SLTP validation (Buy→bid, Sell→ask)
  const markPrice = Number(isBuy ? liveBid : liveAsk) || 0;
  const entryFx = Number(isBuy ? liveEa : liveEb) || 1;
  const symbolFx = Number(selectedSymbolExchangeRate) || 1;
  const quoteFx = Number(selectedSymbolQuoteExchangeRate) || 1;
  const qty = Number(quantity) || 0;
  const balance = Number(metrics?.balance) || 0;
  const pipSize = useMemo(
    () => resolvePipSize(details, tick, symbolInfo),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [details?.pip_position, details?.digit, tick?.pip_position, tick?.digit, tick?.d, symbolInfo?.pip_position, symbolInfo?.digit]
  );
  const lvg = useMemo(
    () => resolveLeverage(activeLvg, leverage, leverageData),
    [activeLvg, leverage, leverageData]
  );

  const requiredMargin = useMemo(() => {
    if (!(qty > 0 && entryPrice > 0 && lvg > 0)) return 0;
    return qty * ((entryPrice * symbolFx * entryFx) / lvg);
  }, [qty, entryPrice, symbolFx, entryFx, lvg]);

  const freeMargin = Number(metrics?.freeMargin);
  const marginTooLow = requiredMargin > 0 && requiredMargin < MIN_REQUIRED_MARGIN;
  const marginExceedsFree =
    requiredMargin > 0 && Number.isFinite(freeMargin) && requiredMargin > freeMargin;
  const canCalc = qty > 0 && entryPrice > 0 && pipSize > 0;

  const [sl, setSl] = useState({ pips: '', price: '', balance: '', pnl: slPnl || '' });
  const [tp, setTp] = useState({ pips: '', price: '', balance: '', pnl: tpPnl || '' });
  const [slErr, setSlErr] = useState('');
  const [tpErr, setTpErr] = useState('');

  // Soft hint only when SL price sits between bid and ask (not pip-vs-spread —
  // inverted/corrupt quotes make |ask-bid| huge and falsely flag valid stops).
  const slInsideSpread =
    slOn &&
    liveAsk > 0 &&
    liveBid > 0 &&
    liveAsk > liveBid &&
    Number(sl.price) > 0 &&
    Number(sl.price) > liveBid &&
    Number(sl.price) < liveAsk;

  const slDriver = useRef(Number(slPnl) > 0 ? 'pnl' : '');
  const tpDriver = useRef(Number(tpPnl) > 0 ? 'pnl' : '');
  // Last committed driver values (survive re-renders / live tick updates).
  // Start empty — do not invent 50/100 pips; user must type like market-order SLTP.
  const slVal = useRef({
    pips: Number(legacySlPips) > 0 ? Number(legacySlPips) : 0,
    price: 0,
    balance: 0,
    pnl: Number(slPnl) > 0 ? Number(slPnl) : 0,
  });
  const tpVal = useRef({
    pips: Number(legacyTpPips) > 0 ? Number(legacyTpPips) : 0,
    price: 0,
    balance: 0,
    pnl: Number(tpPnl) > 0 ? Number(tpPnl) : 0,
  });

  const currencySymbol = metrics?.userCurrencySymbol || user?.userCurrencySymbol || '';

  // Publish the matching risk snapshot before paint, so a keystroke never
  // briefly renders a stale-calculation error between the two state updates.
  useLayoutEffect(() => {
    onMarginChange?.({
      ready: canCalc && Number.isFinite(requiredMargin) && requiredMargin > 0 && Number.isFinite(freeMargin),
      symbol,
      quantity: qty,
      direction,
      slOn,
      tpOn,
      slPnl,
      tpPnl,
      requiredMargin,
      maxQuantity: entryPrice > 0 && lvg > 0 && symbolFx > 0 && entryFx > 0 && Number.isFinite(freeMargin)
        ? Math.max(0, Math.floor(freeMargin / ((entryPrice * symbolFx * entryFx) / lvg))) : null,
      freeMargin: Number.isFinite(freeMargin) ? freeMargin : 0,
      leverage: lvg,
      entryPrice,
      tooLow: marginTooLow,
      exceedsFree: marginExceedsFree,
      minRequired: MIN_REQUIRED_MARGIN,
      slInvalid: Boolean(slOn && slErr),
      tpInvalid: Boolean(tpOn && tpErr),
      sltpError: (slOn && slErr) || (tpOn && tpErr) || '',
    });
  }, [
    canCalc, symbol, qty, direction, slPnl, tpPnl,
    symbolFx, entryFx,
    requiredMargin,
    freeMargin,
    lvg,
    entryPrice,
    marginTooLow,
    marginExceedsFree,
    slOn,
    tpOn,
    slErr,
    tpErr,
    onMarginChange,
  ]);

  /** OrderSLTP: account PNL from price distance */
  const pnlFromPrice = useCallback(
    (price) => {
      const pnlQuote = qty * Math.abs(entryPrice - Number(price));
      return pnlQuote * symbolFx * entryFx;
    },
    [qty, entryPrice, symbolFx, entryFx]
  );

  /** OrderSLTP: price from account PNL / balance% (uses quoteFx / entryFx) */
  const priceOffsetFromPnl = useCallback(
    (pnlAcct) => {
      if (!(qty > 0)) return 0;
      return (Number(pnlAcct) / qty) * quoteFx * (1 / (entryFx || 1));
    },
    [qty, quoteFx, entryFx]
  );

  const validateSlPrice = useCallback(
    (p) => {
      const price = Number(p);
      if (!Number.isFinite(price) || price <= 0) return t('ea.validSlPrice');
      if (!(markPrice > 0)) return '';
      // Buy: SL < bid; Sell: SL > ask (same as open-position / assertPositionSLTP)
      if (isBuy && price >= markPrice) return t('validation.buySL');
      if (!isBuy && price <= markPrice) return t('validation.sellSL');
      return '';
    },
    [isBuy, markPrice, t]
  );

  const validateTpPrice = useCallback(
    (p) => {
      const price = Number(p);
      if (!Number.isFinite(price) || price <= 0) return t('ea.validTpPrice');
      if (!(markPrice > 0)) return '';
      if (isBuy && price <= markPrice) return t('validation.buyTP');
      if (!isBuy && price >= markPrice) return t('validation.sellTP');
      return '';
    },
    [isBuy, markPrice, t]
  );

  const fillSlFromPrice = useCallback(
    (price) => {
      const p = Number(price);
      if (!canCalc || !Number.isFinite(p)) return;
      const distance = isBuy ? entryPrice - p : p - entryPrice;
      const pips = distance / pipSize;
      const pnl = pnlFromPrice(p);
      const balPct = balance > 0 ? (pnl / balance) * 100 : 0;
      setSlErr(validateSlPrice(p));
      slVal.current = { pips, price: p, balance: balPct, pnl };
      setSl({ pips, price: p, balance: balPct, pnl });
      commitPnl(pnl, onSlPnlChange);
    },
    [canCalc, isBuy, entryPrice, pipSize, pnlFromPrice, balance, onSlPnlChange, validateSlPrice]
  );

  const fillTpFromPrice = useCallback(
    (price) => {
      const p = Number(price);
      if (!canCalc || !Number.isFinite(p)) return;
      const pips = Math.abs(entryPrice - p) / pipSize;
      const pnl = pnlFromPrice(p);
      const balPct = balance > 0 ? (pnl / balance) * 100 : 0;
      setTpErr(validateTpPrice(p));
      tpVal.current = { pips, price: p, balance: balPct, pnl };
      setTp({ pips, price: p, balance: balPct, pnl });
      commitPnl(pnl, onTpPnlChange);
    },
    [canCalc, isBuy, entryPrice, pipSize, pnlFromPrice, balance, onTpPnlChange, validateTpPrice]
  );

  // Re-check SL/TP against live bid/ask whenever the mark moves (same as open position)
  useEffect(() => {
    if (!canCalc || !(markPrice > 0)) return;
    if (slOn && Number(slVal.current.price) > 0) {
      setSlErr(validateSlPrice(slVal.current.price));
    } else if (!slOn) {
      setSlErr('');
    }
    if (tpOn && Number(tpVal.current.price) > 0) {
      setTpErr(validateTpPrice(tpVal.current.price));
    } else if (!tpOn) {
      setTpErr('');
    }
  }, [markPrice, canCalc, slOn, tpOn, validateSlPrice, validateTpPrice]);

  const applySl = useCallback(
    (driver, raw) => {
      if (!canCalc) return;
      const v = Number(raw);
      if (!Number.isFinite(v)) return;
      slDriver.current = driver;

      if (driver === 'pips') {
        if (v <= 0) return;
        const price = isBuy ? entryPrice - pipSize * v : entryPrice + pipSize * v;
        fillSlFromPrice(price);
        slVal.current.pips = v;
        setSl((prev) => ({ ...prev, pips: v }));
        return;
      }
      if (driver === 'price') {
        fillSlFromPrice(v);
        return;
      }
      if (driver === 'balance') {
        if (!(balance > 0)) return;
        const pnl = (balance / 100) * v;
        const price = isBuy
          ? entryPrice - priceOffsetFromPnl(pnl)
          : entryPrice + priceOffsetFromPnl(pnl);
        fillSlFromPrice(price);
        return;
      }
      if (driver === 'pnl') {
        const price = isBuy
          ? entryPrice - priceOffsetFromPnl(v)
          : entryPrice + priceOffsetFromPnl(v);
        fillSlFromPrice(price);
      }
    },
    [
      canCalc,
      isBuy,
      entryPrice,
      pipSize,
      balance,
      fillSlFromPrice,
      priceOffsetFromPnl,
    ]
  );

  const applyTp = useCallback(
    (driver, raw) => {
      if (!canCalc) return;
      const v = Number(raw);
      if (!Number.isFinite(v)) return;
      tpDriver.current = driver;

      if (driver === 'pips') {
        if (v <= 0) return;
        const price = isBuy ? entryPrice + pipSize * v : entryPrice - pipSize * v;
        fillTpFromPrice(price);
        tpVal.current.pips = v;
        setTp((prev) => ({ ...prev, pips: v }));
        return;
      }
      if (driver === 'price') {
        fillTpFromPrice(v);
        return;
      }
      if (driver === 'balance') {
        if (!(balance > 0)) return;
        const pnl = (balance / 100) * v;
        const price = isBuy
          ? entryPrice + priceOffsetFromPnl(pnl)
          : entryPrice - priceOffsetFromPnl(pnl);
        fillTpFromPrice(price);
        return;
      }
      if (driver === 'pnl') {
        const price = isBuy
          ? entryPrice + priceOffsetFromPnl(v)
          : entryPrice - priceOffsetFromPnl(v);
        fillTpFromPrice(price);
      }
    },
    [
      canCalc,
      isBuy,
      entryPrice,
      pipSize,
      balance,
      fillTpFromPrice,
      priceOffsetFromPnl,
    ]
  );

  // Disabling a side clears its draft even while quotes/calculation are unavailable.
  useEffect(() => {
    if (slOn) return;
    slDriver.current = '';
    slVal.current = { pips: 0, price: 0, balance: 0, pnl: 0 };
    setSl({ pips: '', price: '', balance: '', pnl: '' });
    setSlErr('');
  }, [slOn]);

  useEffect(() => {
    if (tpOn) return;
    tpDriver.current = '';
    tpVal.current = { pips: 0, price: 0, balance: 0, pnl: 0 };
    setTp({ pips: '', price: '', balance: '', pnl: '' });
    setTpErr('');
  }, [tpOn]);

  // Recompute from the ACTIVE driver when qty / live price / FX / pip size changes.
  // Only when the user (or edit-seed) already entered a value — never invent defaults.
  useEffect(() => {
    if (!canCalc) return;
    if (slOn) {
      const d = slDriver.current;
      const src = slVal.current;
      if (d === 'pips' && Number(src.pips) > 0) applySl('pips', src.pips);
      else if (d === 'price' && Number(src.price) > 0) applySl('price', src.price);
      else if (d === 'balance' && Number(src.balance) > 0) applySl('balance', src.balance);
      else if (d === 'pnl' && Number(src.pnl || slPnl) > 0) applySl('pnl', src.pnl || slPnl);
    }
    if (tpOn) {
      const d = tpDriver.current;
      const src = tpVal.current;
      if (d === 'pips' && Number(src.pips) > 0) applyTp('pips', src.pips);
      else if (d === 'price' && Number(src.price) > 0) applyTp('price', src.price);
      else if (d === 'balance' && Number(src.balance) > 0) applyTp('balance', src.balance);
      else if (d === 'pnl' && Number(src.pnl || tpPnl) > 0) applyTp('pnl', src.pnl || tpPnl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty, entryPrice, isBuy, pipSize, balance, symbolFx, entryFx, quoteFx, slOn, tpOn, canCalc]);

  // Seed once per symbol / direction / toggles (do NOT include live slPnl/tpPnl —
  // commitPnl updates those and would re-seed mid-typing, forcing the pnl driver).
  // No saved value → leave inputs empty (placeholder only), same as market-order SLTP.
  const seeded = useRef('');
  useEffect(() => {
    if (!canCalc) return;
    const key = `${symbol}|${slOn}|${tpOn}|${isBuy}`;
    if (seeded.current === key) return;
    seeded.current = key;
    if (slOn) {
      if (Number(slPnl) > 0) {
        slDriver.current = 'pnl';
        slVal.current.pnl = Number(slPnl);
        applySl('pnl', slVal.current.pnl);
      } else if (Number(legacySlPips) > 0) {
        slDriver.current = 'pips';
        slVal.current.pips = Number(legacySlPips);
        applySl('pips', slVal.current.pips);
      } else {
        slDriver.current = '';
        slVal.current = { pips: 0, price: 0, balance: 0, pnl: 0 };
        setSl({ pips: '', price: '', balance: '', pnl: '' });
        setSlErr('');
      }
    } else {
      slDriver.current = '';
      slVal.current = { pips: 0, price: 0, balance: 0, pnl: 0 };
      setSl({ pips: '', price: '', balance: '', pnl: '' });
      setSlErr('');
    }
    if (tpOn) {
      if (Number(tpPnl) > 0) {
        tpDriver.current = 'pnl';
        tpVal.current.pnl = Number(tpPnl);
        applyTp('pnl', tpVal.current.pnl);
      } else if (Number(legacyTpPips) > 0) {
        tpDriver.current = 'pips';
        tpVal.current.pips = Number(legacyTpPips);
        applyTp('pips', tpVal.current.pips);
      } else {
        tpDriver.current = '';
        tpVal.current = { pips: 0, price: 0, balance: 0, pnl: 0 };
        setTp({ pips: '', price: '', balance: '', pnl: '' });
        setTpErr('');
      }
    } else {
      tpDriver.current = '';
      tpVal.current = { pips: 0, price: 0, balance: 0, pnl: 0 };
      setTp({ pips: '', price: '', balance: '', pnl: '' });
      setTpErr('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, slOn, tpOn, isBuy, canCalc]);

  const disabledSl = !slOn || !canCalc;
  const disabledTp = !tpOn || !canCalc;

  const onField = (side, field) => (e) => {
    const typed = e.target.value;
    if (typed === '' || Number(typed) === 0) {
      const empty = { pips: 0, price: 0, balance: 0, pnl: 0 };
      if (side === 'sl') {
        slDriver.current = ''; slVal.current = empty;
        setSl({ ...empty, [field]: typed }); setSlErr(''); onSlPnlChange('');
      } else {
        tpDriver.current = ''; tpVal.current = empty;
        setTp({ ...empty, [field]: typed }); setTpErr(''); onTpPnlChange('');
      }
      return;
    }
    const raw = parseInput(typed);
    if (typeof raw !== 'number' || raw < 0) {
      const empty = { pips: 0, price: 0, balance: 0, pnl: 0 };
      if (side === 'sl') {
        slDriver.current = ''; slVal.current = empty;
        setSl({ ...empty, [field]: typed });
        setSlErr(t('ea.validSlPrice')); onSlPnlChange('');
      } else {
        tpDriver.current = ''; tpVal.current = empty;
        setTp({ ...empty, [field]: typed });
        setTpErr(t('ea.validTpPrice')); onTpPnlChange('');
      }
      return;
    }
    if (side === 'sl') {
      slVal.current[field] = raw;
      applySl(field, raw);
      setSl((prev) => ({ ...prev, [field]: typed }));
    } else {
      tpVal.current[field] = raw;
      applyTp(field, raw);
      setTp((prev) => ({ ...prev, [field]: typed }));
    }
  };

  const slMessage = slOn ? (slErr || slError || '') : '';
  const tpMessage = tpOn ? (tpErr || tpError || '') : '';

  return (
    <>
      <div className={s.marginRow}>
        <div className={s.marginItem}>
          <span className={s.label}>{t('ea.entryLive')}</span>
          <strong>{entryPrice > 0 ? displayNum(entryPrice) : '—'}</strong>
        </div>
        <div className={s.marginItem}>
          <span className={s.label}>{t('orderPanel.leverage')}</span>
          <strong>1:{lvg}</strong>
        </div>
        <div className={s.marginItem}>
          <span className={s.label}>{t('metrics.freeMargin')}</span>
          <strong>
            {currencySymbol}
            {formatMargin(Number.isFinite(freeMargin) ? freeMargin : 0)}
          </strong>
        </div>
        <div className={`${s.marginItem} ${marginTooLow || marginExceedsFree ? s.marginBad : ''}`}>
          <span className={s.label}>{t('marketOrder.requiredMargin')}</span>
          <strong>
            {currencySymbol}
            {formatMargin(requiredMargin)}
          </strong>
        </div>
      </div>
      {marginError}
      {toggles}
      {slInsideSpread && !slErr && (
        <div className={s.hint}>
          {t('ea.slInsideSpread', { hint: isBuy ? t('ea.belowBid') : t('ea.aboveAsk') })}
        </div>
      )}
      {!canCalc && (
        <div className={s.hint}>
          {t('ea.waitingLivePrice', { symbol: symbol || 'symbol' })}
        </div>
      )}

      {(slOn || tpOn) && (
        <div className={`sltp-grid ${s.sltpGrid}`}>
          <div className={s.sltpSharedLabels} aria-hidden="true">
            <span>{t('orderPanel.pips')}</span>
            <span>{t('orderPanel.price')}</span>
            <span>{t('orderPanel.percentBalance')}</span>
            <span>{t('orderPanel.profitApprox', { symbol: currencySymbol })}</span>
          </div>
          <div data-risk="sl" className={`column left ${s.sltpCol}`}>
            <div className={s.riskError} aria-hidden="true" />
            <div className={`input-group ${s.sltpField}`}>
              <label>{t('ea.slPips')}</label>
              <input
                type="number"
                min="1"
                step="1"
                disabled={disabledSl}
                className={disabledSl ? 'disabled-fields' : ''}
                value={displayNum(sl.pips, 2)}
                aria-label={t('ea.slPips')}
                placeholder="0"
                onChange={onField('sl', 'pips')}
              />
            </div>
            <div className={`input-group ${s.sltpField}`}>
              <label>{t('ea.slPrice')}</label>
              <input
                type="number"
                step={pipSize}
                disabled={disabledSl}
                className={(disabledSl ? 'disabled-fields ' : '') + (slMessage ? s.sltpPriceError : '')}
                value={displayNum(sl.price)}
                aria-label={t('ea.slPrice')}
                placeholder="0"
                data-tooltip={slMessage || undefined}
                data-strategy-error={slError ? 'sl' : undefined}
                aria-invalid={Boolean(slMessage)}
                title={slMessage || undefined}
                onChange={onField('sl', 'price')}
              />
            </div>
            <div className={`input-group ${s.sltpField}`}>
              <label>{t('ea.slBalance')}</label>
              <input
                type="number"
                step="0.01"
                disabled={disabledSl}
                className={disabledSl ? 'disabled-fields' : ''}
                value={displayNum(sl.balance, 2)}
                aria-label={t('ea.slBalance')}
                placeholder="0"
                onChange={onField('sl', 'balance')}
              />
            </div>
            <div className={`input-group ${s.sltpField}`}>
              <label>{t('ea.slPnlCurrency', { currency: currencySymbol })}</label>
              <input
                type="number"
                step="any"
                disabled={disabledSl}
                className={disabledSl ? 'disabled-fields' : ''}
                value={displayNum(sl.pnl, 4)}
                aria-label={t('ea.slPnlCurrency', { currency: currencySymbol })}
                placeholder="0"
                onChange={onField('sl', 'pnl')}
              />
            </div>
          </div>

          <div data-risk="tp" className={`column right ${s.sltpCol}`}>
            <div className={s.riskError} aria-hidden="true" />
            <div className={`input-group ${s.sltpField}`}>
              <label>{t('ea.tpPips')}</label>
              <input
                type="number"
                min="1"
                step="1"
                disabled={disabledTp}
                className={disabledTp ? 'disabled-fields' : ''}
                value={displayNum(tp.pips, 2)}
                aria-label={t('ea.tpPips')}
                placeholder="0"
                onChange={onField('tp', 'pips')}
              />
            </div>
            <div className={`input-group ${s.sltpField}`}>
              <label>{t('ea.tpPrice')}</label>
              <input
                type="number"
                step={pipSize}
                disabled={disabledTp}
                className={(disabledTp ? 'disabled-fields ' : '') + (tpMessage ? s.sltpPriceError : '')}
                value={displayNum(tp.price)}
                aria-label={t('ea.tpPrice')}
                placeholder="0"
                data-tooltip={tpMessage || undefined}
                data-strategy-error={tpError ? 'tp' : undefined}
                aria-invalid={Boolean(tpMessage)}
                title={tpMessage || undefined}
                onChange={onField('tp', 'price')}
              />
            </div>
            <div className={`input-group ${s.sltpField}`}>
              <label>{t('ea.tpBalance')}</label>
              <input
                type="number"
                step="0.01"
                disabled={disabledTp}
                className={disabledTp ? 'disabled-fields' : ''}
                value={displayNum(tp.balance, 2)}
                aria-label={t('ea.tpBalance')}
                placeholder="0"
                onChange={onField('tp', 'balance')}
              />
            </div>
            <div className={`input-group ${s.sltpField}`}>
              <label>{t('ea.tpPnlCurrency', { currency: currencySymbol })}</label>
              <input
                type="number"
                step="any"
                disabled={disabledTp}
                className={disabledTp ? 'disabled-fields' : ''}
                value={displayNum(tp.pnl, 4)}
                aria-label={t('ea.tpPnlCurrency', { currency: currencySymbol })}
                placeholder="0"
                onChange={onField('tp', 'pnl')}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
